import {
  addDays,
  convertWeight,
  ExerciseList,
  MUSCLE_GROUP_IDS,
  MUSCLE_GROUP_NAMES,
  ProgressResponse,
  startOfWeek,
  type MuscleGroup,
  type ProgressWeek,
  type User,
} from '@squats/shared';
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useUser } from '../auth.tsx';
import { useCachedApi } from '../cache.ts';
import { PersonPicker, usePeople } from '../components/People.tsx';
import { TrendChart } from '../components/TrendChart.tsx';
import { formatMonthDay } from '../dates.ts';

const RANGES = [
  { label: '3 months', weeks: 12 },
  { label: '6 months', weeks: 26 },
  { label: 'Year', weeks: 52 },
] as const;
const GRID_WEEKS = 8;
const GROUPS = MUSCLE_GROUP_IDS.filter((g) => g !== 'cardio');
const fmtSets = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Training volume per muscle group, consistency, and the main lifts over time; yours or anyone's in the group. */
export function ProgressPage() {
  const user = useUser();
  const people = usePeople(user);
  const [who, setWho] = useState(user.id);
  const [weeks, setWeeks] = useState<number>(12);
  const { data, error } = useCachedApi(user.id, `/api/progress?user=${who}&weeks=${weeks}`, ProgressResponse);
  const progress = data?.userId === who ? data : undefined;
  const name = who === user.id ? 'You' : (people.find((p) => p.id === who)?.displayName ?? 'They');
  const nothing = progress && !progress.days.length && progress.streak.longest === 0;

  return (
    <div className="stack-lg">
      <div className="row wrap">
        <h1 className="grow">Progress</h1>
        <PersonPicker user={user} people={people} value={who} onChange={setWho} label="Whose progress" />
      </div>

      {error && !progress && <p className="error">{error}</p>}
      {!progress && !error && <p className="muted">Loading…</p>}
      {nothing && <p className="card muted">{name === 'You' ? 'Log a workout and your progress shows up here.' : `${name} hasn’t logged a workout yet.`}</p>}

      {progress && !nothing && (
        <>
          <Consistency progress={progress} />
          <Volume progress={progress} />
          <section className="card stack" aria-labelledby="lifts-h">
            <h2 id="lifts-h">Main lifts</h2>
            <span className="chips" role="group" aria-label="Time range">
              {RANGES.map((r) => (
                <button key={r.weeks} type="button" className={`chip toggle${weeks === r.weeks ? ' on' : ''}`} aria-pressed={weeks === r.weeks} onClick={() => setWeeks(r.weeks)}>
                  {r.label}
                </button>
              ))}
            </span>
            <Lifts user={user} progress={progress} />
          </section>
        </>
      )}
    </div>
  );
}

function Consistency({ progress }: { progress: ProgressResponse }) {
  const thisWeek = progress.weeks.at(-1)!;
  return (
    <section className="card stack" aria-labelledby="streak-h">
      <h2 id="streak-h">Consistency</h2>
      <div className="stats">
        <div className="stat">
          <span className="stat-value">{progress.streak.current}</span>
          <span className="muted small">week{progress.streak.current === 1 ? '' : 's'} in a row</span>
        </div>
        <div className="stat">
          <span className="stat-value">{progress.streak.longest}</span>
          <span className="muted small">longest streak</span>
        </div>
        <div className="stat">
          <span className="stat-value">{thisWeek.workouts}</span>
          <span className="muted small">workout{thisWeek.workouts === 1 ? '' : 's'} this week</span>
        </div>
      </div>
      <CalendarGrid progress={progress} />
      <p className="muted small">A streak counts weeks (Sunday to Saturday) with at least one workout, so rest days never break it.</p>
    </section>
  );
}

/** Six months of training days, a column per week (Sunday at the top), shaded by working sets. */
function CalendarGrid({ progress }: { progress: ProgressResponse }) {
  const WEEKS = 26;
  const CELL = 12;
  const GAP = 3;
  const first = addDays(startOfWeek(progress.today), -7 * (WEEKS - 1));
  const byDate = new Map(progress.days.map((d) => [d.date, d]));
  const level = (sets: number) => (sets >= 20 ? 4 : sets >= 12 ? 3 : sets >= 6 ? 2 : 1);
  const width = WEEKS * (CELL + GAP) - GAP;
  const cells = [];
  const months: { x: number; label: string }[] = [];
  for (let w = 0; w < WEEKS; w++) {
    const weekStart = addDays(first, 7 * w);
    // Label a column where a new month starts.
    if (w === 0 || weekStart.slice(5, 7) !== addDays(weekStart, -7).slice(5, 7)) months.push({ x: w * (CELL + GAP), label: formatMonthDay(weekStart).split(' ')[0]! });
    for (let d = 0; d < 7; d++) {
      const date = addDays(weekStart, d);
      if (date > progress.today) continue;
      const day = byDate.get(date);
      cells.push(
        <rect
          key={date}
          x={w * (CELL + GAP)}
          y={14 + d * (CELL + GAP)}
          width={CELL}
          height={CELL}
          rx={3}
          className={`cal-cell${day ? ` lvl-${level(day.sets)}` : ''}${date === progress.today ? ' today' : ''}`}
        >
          <title>{day ? `${formatMonthDay(date)}: ${day.workouts} workout${day.workouts > 1 ? 's' : ''}, ${day.sets} sets` : formatMonthDay(date)}</title>
        </rect>,
      );
    }
  }
  return (
    <svg className="cal-grid" viewBox={`0 0 ${width} ${14 + 7 * (CELL + GAP)}`} role="img" aria-label={`Training days over the last six months: ${progress.days.length} days`}>
      {months.slice(months[1] && months[1].x - months[0]!.x < 30 ? 1 : 0).map((m) => (
        <text key={m.x} x={Math.min(m.x, width - 18)} y={10} className="trend-axis">
          {m.label}
        </text>
      ))}
      {cells}
    </svg>
  );
}

function Volume({ progress }: { progress: ProgressResponse }) {
  const [showWeek, setShowWeek] = useState(progress.weeks.length - 1);
  const week = progress.weeks[Math.min(showWeek, progress.weeks.length - 1)]!;
  const index = progress.weeks.indexOf(week);
  // Average of the four weeks before the one shown, for comparison.
  const before = progress.weeks.slice(Math.max(0, index - 4), index);
  const avg = (g: MuscleGroup) => (before.length ? before.reduce((sum, w) => sum + (w.muscles[g] ?? 0), 0) / before.length : 0);
  const rows = GROUPS.map((g) => ({ g, sets: week.muscles[g] ?? 0, avg: avg(g) }))
    .filter((r) => r.sets > 0 || r.avg > 0)
    .sort((a, b) => b.sets - a.sets || b.avg - a.avg);
  const max = Math.max(10, ...rows.map((r) => Math.max(r.sets, r.avg)));
  const isThisWeek = index === progress.weeks.length - 1;

  return (
    <section className="card stack" aria-labelledby="volume-h">
      <h2 id="volume-h">Weekly volume</h2>
      <div className="row week-nav-row">
        <button type="button" className="btn small" onClick={() => setShowWeek(index - 1)} disabled={index === 0} aria-label="Previous week">
          ‹
        </button>
        <span className="small week-label">{isThisWeek ? 'This week' : `Week of ${formatMonthDay(week.start)}`}</span>
        <button type="button" className="btn small" onClick={() => setShowWeek(index + 1)} disabled={isThisWeek} aria-label="Next week">
          ›
        </button>
      </div>
      <p className="muted small">Working sets per muscle group: a set counts fully for the main muscles and half for the helpers. The tick is the average of the 4 weeks before.</p>
      {rows.length ? (
        <ul className="volume-bars">
          {rows.map((r) => (
            <li key={r.g}>
              <span className="vb-name">{MUSCLE_GROUP_NAMES[r.g]}</span>
              <span className="vb-track">
                <span className="vb-bar" style={{ width: `${(r.sets / max) * 100}%` }} />
                {r.avg > 0 && <span className="vb-avg" style={{ left: `${(r.avg / max) * 100}%` }} title={`4-week average: ${fmtSets(r.avg)}`} />}
              </span>
              <span className="vb-num">{fmtSets(r.sets)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No sets logged {isThisWeek ? 'yet this week' : 'that week'}.</p>
      )}
      <VolumeGrid weeks={progress.weeks.slice(-GRID_WEEKS)} />
    </section>
  );
}

/** The last 8 weeks side by side: a row per muscle group, shaded by sets. */
function VolumeGrid({ weeks }: { weeks: ProgressWeek[] }) {
  const groups = GROUPS.filter((g) => weeks.some((w) => (w.muscles[g] ?? 0) > 0));
  if (!groups.length) return null;
  const max = Math.max(...weeks.flatMap((w) => groups.map((g) => w.muscles[g] ?? 0)));
  return (
    <div className="volume-grid-wrap">
      <table className="volume-grid">
        <caption className="muted small">Sets per week (weeks starting Sunday), last {weeks.length} weeks</caption>
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Muscle group</span>
            </th>
            {weeks.map((w, i) => {
              // Day of the month, with the month above it where it changes.
              const [month, day] = formatMonthDay(w.start).split(' ');
              const newMonth = i === 0 || w.start.slice(5, 7) !== weeks[i - 1]!.start.slice(5, 7);
              return (
                <th key={w.start} scope="col" className="muted" title={`Week of ${formatMonthDay(w.start)}`}>
                  <span className="vg-month">{newMonth ? month : '\u00a0'}</span>
                  {day}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g}>
              <th scope="row">{MUSCLE_GROUP_NAMES[g]}</th>
              {weeks.map((w) => {
                const n = w.muscles[g] ?? 0;
                return (
                  <td key={w.start} className={n ? 'vg-cell' : 'vg-cell empty'} style={n ? ({ '--shade': `${Math.round((n / max) * 100)}%` } as React.CSSProperties) : undefined}>
                    {n ? fmtSets(n) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Lifts({ user, progress }: { user: User; progress: ProgressResponse }) {
  const { data } = useCachedApi(user.id, '/api/exercises', ExerciseList);
  const names = useMemo(() => new Map((data?.exercises ?? []).map((e) => [e.id, e.name])), [data]);
  if (!progress.lifts.length) return <p className="muted">No weighted lifts logged in this period.</p>;
  const shown = (kg: number) => Math.round(convertWeight(kg, 'kg', user.units));
  return (
    <div className="lifts">
      {progress.lifts.map((l) => {
        const first = l.points[0]!;
        const last = l.points.at(-1)!;
        const change = shown(last.value) - shown(first.value);
        return (
          <div key={l.exerciseId} className="lift">
            <div className="row">
              <Link href={`/exercises/${l.exerciseId}`} className="grow row-title">
                {names.get(l.exerciseId) ?? 'Exercise'}
              </Link>
              <span>
                <strong>
                  {shown(last.value)} {user.units}
                </strong>
                {l.points.length > 1 && change !== 0 && (
                  <span className={`muted small${change > 0 ? ' up' : ''}`}>
                    {' '}
                    {change > 0 ? '+' : '−'}
                    {Math.abs(change)}
                  </span>
                )}
              </span>
            </div>
            {l.points.length > 1 ? (
              <TrendChart
                points={l.points.map((p) => ({ date: p.date, value: shown(p.value) }))}
                label={`${names.get(l.exerciseId) ?? 'Lift'}, best estimated 1RM`}
                format={(v) => `${v}`}
              />
            ) : (
              <p className="muted small">One session so far.</p>
            )}
            <p className="muted small">
              Best est. 1RM per session · {l.sessions} session{l.sessions === 1 ? '' : 's'}
            </p>
          </div>
        );
      })}
    </div>
  );
}
